/**
 * "Minimal Light" — airy editorial design on cream. Centered composition,
 * thin rules, generous whitespace. Photos sit inside a framed window on the
 * cover; species slides use a solid light caption band instead of a dark
 * gradient so the photo stays untouched.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SpeciesSlideData, type ClosingSlideData, type SlideStyle, type DrawOpts } from "../types";
import { drawReadabilityGradient, drawWatermark, truncateToWidth, FLAT_BG_COLORS } from "../shared";

const CREAM = "#faf7f0";
const INK = "#22271f";
const INK_SOFT = "rgba(34,39,31,0.55)";
const RULE = "rgba(34,39,31,0.22)";

/** One centered, middle-anchored line's box — same shape as field-diary's
 * ClosingTextBox/SpeciesNameBox, single source of truth shared by the draw
 * functions below and MinimalLightSpeciesOverlay/MinimalLightClosingOverlay. */
export interface MinimalTextBox {
  top: number; fontSize: number; fontWeight: number; family: "display" | "body"; color: string;
}

export function minimalSpeciesLayout(data: SpeciesSlideData): {
  primary: MinimalTextBox | null;
  secondary: MinimalTextBox | null;
  tertiary: MinimalTextBox | null;
} {
  const BAND_H = 300;
  let y = SLIDE_H - BAND_H + 86;

  let primary: MinimalTextBox | null = null;
  if (data.namePrimary) {
    primary = { top: y, fontSize: 64, fontWeight: 400, family: "display", color: INK };
    y += 74;
  }
  let secondary: MinimalTextBox | null = null;
  if (data.nameSecondary) {
    secondary = { top: y, fontSize: 34, fontWeight: 400, family: "body", color: "rgba(34,39,31,0.75)" };
    y += 52;
  }
  const tertiary: MinimalTextBox | null = data.nameTertiary
    ? { top: y, fontSize: 30, fontWeight: 400, family: "body", color: INK_SOFT }
    : null;

  return { primary, secondary, tertiary };
}

export function minimalClosingLayout(data: ClosingSlideData): {
  heading: MinimalTextBox;
  subtext: MinimalTextBox;
  handle: MinimalTextBox | null;
} {
  const H = SLIDE_H;
  return {
    heading: { top: H / 2 - 70, fontSize: 88, fontWeight: 400, family: "display", color: INK },
    subtext: { top: H / 2 + 40, fontSize: 32, fontWeight: 400, family: "body", color: INK_SOFT },
    handle: data.handle
      ? { top: H / 2 + 140, fontSize: 32, fontWeight: 600, family: "body", color: INK }
      : null,
  };
}

/** cover-fit into an arbitrary rect (not the full slide); same offsetY/zoom semantics as drawCoverFitImage. */
function drawImageInRect(
  ctx: CanvasRenderingContext2D, img: HTMLImageElement,
  x: number, y: number, w: number, h: number,
  offsetY = 0, zoom = 1
): void {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight) * Math.max(1, zoom);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  const slack = (dh - h) / 2;
  const dy = y + (h - dh) / 2 - Math.max(-1, Math.min(1, offsetY)) * slack;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(img, x + (w - dw) / 2, dy, dw, dh);
  ctx.restore();
}

function rule(ctx: CanvasRenderingContext2D, x1: number, x2: number, y: number, color: string = RULE): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x1, y);
  ctx.lineTo(x2, y);
  ctx.stroke();
}

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 72;
  const cx = W / 2;

  ctx.clearRect(0, 0, W, H);
  // Flat colors/transparent are ignored once a photo is set — this template
  // always keeps cream margins around the framed photo window regardless.
  const noPhotoMode = data.photo ? undefined : data.backgroundMode;
  const transparentMode = noPhotoMode === "transparent";
  const flatColor = noPhotoMode === "light" || noPhotoMode === "dark" || noPhotoMode === "accent"
    ? FLAT_BG_COLORS[noPhotoMode]
    : null;
  // Ink-on-cream is this template's default; only a dark/accent flat color
  // or the see-through transparent option need white text instead.
  const needsLightText = transparentMode || noPhotoMode === "dark" || noPhotoMode === "accent";

  if (transparentMode) {
    drawReadabilityGradient(ctx, true);
  } else if (flatColor) {
    ctx.fillStyle = flatColor;
    ctx.fillRect(0, 0, W, H);
  } else {
    ctx.fillStyle = CREAM;
    ctx.fillRect(0, 0, W, H);
  }

  const textMain = needsLightText ? "#fff" : INK;
  const textSoft = needsLightText ? "rgba(255,255,255,0.72)" : INK_SOFT;
  const ruleColor = needsLightText ? "rgba(255,255,255,0.35)" : RULE;

  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  // Kicker — small, tracked out
  ctx.font = `500 28px ${style.fonts.body}`;
  ctx.fillStyle = textSoft;
  ctx.fillText(data.kickerText.toUpperCase().split("").join(" "), cx, PAD);

  rule(ctx, cx - 40, cx + 40, PAD + 62, ruleColor);

  // Headline — centered serif
  const titleSize = Math.min(data.headlineFontSize, 120);
  ctx.font = `400 ${titleSize}px ${style.fonts.display}`;
  ctx.fillStyle = textMain;
  ctx.fillText(truncateToWidth(ctx, data.headlineTitle, W - PAD * 2), cx, PAD + 96);

  // Subtitle
  ctx.font = `400 30px ${style.fonts.body}`;
  ctx.fillStyle = textSoft;
  ctx.fillText(data.subtitle, cx, PAD + 96 + titleSize + 26);

  // Framed photo window
  const photoTop = 400;
  const photoBottom = H - 250;
  if (data.photo) {
    drawImageInRect(ctx, data.photo, PAD, photoTop, W - PAD * 2, photoBottom - photoTop, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
  }
  ctx.strokeStyle = ruleColor;
  ctx.lineWidth = 1;
  ctx.strokeRect(PAD, photoTop, W - PAD * 2, photoBottom - photoTop);

  // Stats — one centered line, dot-separated
  if (data.stats.length > 0) {
    ctx.font = `400 28px ${style.fonts.body}`;
    ctx.fillStyle = textSoft;
    const line = data.stats.map((s) => s.value).join("   ·   ");
    ctx.fillText(truncateToWidth(ctx, line, W - PAD * 2), cx, H - 170);
  }

  ctx.textAlign = "left";
  drawWatermark(ctx, style, !needsLightText);
}

function drawSpecies(ctx: CanvasRenderingContext2D, data: SpeciesSlideData, style: SlideStyle, opts?: DrawOpts): void {
  const W = SLIDE_W, H = SLIDE_H;
  const BAND_H = 300;

  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  if (data.photo) {
    drawImageInRect(ctx, data.photo, 0, 0, W, H - BAND_H, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
  }

  // Solid light caption band
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, H - BAND_H, W, BAND_H);
  rule(ctx, 72, W - 72, H - BAND_H);

  // Text: centered, middle-anchored, stacked top-down via minimalSpeciesLayout.
  // A real HTML overlay (MinimalLightSpeciesOverlay.tsx) draws these lines
  // live instead, at the exact same boxes — skip painting them twice.
  if (!opts?.skipEditableText) {
    const cx = W / 2;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const { primary, secondary, tertiary } = minimalSpeciesLayout(data);

    if (primary && data.namePrimary) {
      ctx.font = `${primary.fontWeight} ${primary.fontSize}px ${style.fonts.display}`;
      ctx.fillStyle = primary.color;
      ctx.fillText(truncateToWidth(ctx, data.namePrimary, W - 120), cx, primary.top);
    }
    if (secondary && data.nameSecondary) {
      ctx.font = `${secondary.fontWeight} ${secondary.fontSize}px ${style.fonts.body}`;
      ctx.fillStyle = secondary.color;
      ctx.fillText(truncateToWidth(ctx, data.nameSecondary, W - 120), cx, secondary.top);
    }
    if (tertiary && data.nameTertiary) {
      ctx.font = `${tertiary.fontWeight} ${tertiary.fontSize}px ${style.fonts.body}`;
      ctx.fillStyle = tertiary.color;
      ctx.fillText(truncateToWidth(ctx, data.nameTertiary, W - 120), cx, tertiary.top);
    }

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  drawWatermark(ctx, style, true);
}

function drawClosing(ctx: CanvasRenderingContext2D, data: ClosingSlideData, style: SlideStyle, opts?: DrawOpts): void {
  const W = SLIDE_W, H = SLIDE_H;
  const cx = W / 2;

  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  rule(ctx, cx - 40, cx + 40, H / 2 - 190);
  rule(ctx, cx - 40, cx + 40, H / 2 + 210);

  // A real HTML overlay (MinimalLightClosingOverlay.tsx) draws these lines
  // live instead, at the exact same boxes via minimalClosingLayout() — skip
  // painting them twice.
  if (!opts?.skipEditableText) {
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const { heading, subtext, handle } = minimalClosingLayout(data);

    ctx.font = `${heading.fontWeight} ${heading.fontSize}px ${style.fonts.display}`;
    ctx.fillStyle = heading.color;
    ctx.fillText(truncateToWidth(ctx, data.heading, W - 144), cx, heading.top);

    ctx.font = `${subtext.fontWeight} ${subtext.fontSize}px ${style.fonts.body}`;
    ctx.fillStyle = subtext.color;
    ctx.fillText(data.subtext, cx, subtext.top);

    if (handle && data.handle) {
      ctx.font = `${handle.fontWeight} ${handle.fontSize}px ${style.fonts.body}`;
      ctx.fillStyle = handle.color;
      ctx.fillText(data.handle, cx, handle.top);
    }

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  drawWatermark(ctx, style, true);
}

export const minimalLight: CarouselTemplate = {
  id: "minimal-light",
  name: "Minimal Light",
  premium: false,
  drawCover,
  drawSpecies,
  drawClosing,
  useTextOverlay: { species: true, closing: true },
};
